/**
 * The app-level rule for the setup guide (spec 7.3): send a new user to /welcome, nudge them with a
 * banner, or leave them alone. Pure, so the rules are tested without rendering the app.
 *
 * - 'redirect': a user who has not finished setup lands on the dashboard.
 * - 'banner':   the same user is anywhere else in the app.
 * - 'none':     everyone else - finished, skipped, legacy-wizard users, snoozed, or not yet able to
 *               use the guide (profile still loading, email unverified, account not active).
 */

export const WELCOME_PATH = '/welcome';
const DASHBOARD_PATH = '/dashboard';

/**
 * Links from the guide into the app (the tour's "Open in a new tab") carry this marker. A tab opened
 * that way starts with empty sessionStorage, so without it the dashboard would send the user
 * straight back to the guide they just left to explore.
 */
const GUIDE_ORIGIN_PARAM = 'from';
const GUIDE_ORIGIN_VALUE = 'setup-guide';

export function withGuideOrigin(path: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}${GUIDE_ORIGIN_PARAM}=${GUIDE_ORIGIN_VALUE}`;
}

export function isFromGuide(search: string): boolean {
  return new URLSearchParams(search).get(GUIDE_ORIGIN_PARAM) === GUIDE_ORIGIN_VALUE;
}

export type OnboardingGateDecision = 'redirect' | 'banner' | 'none';

export interface OnboardingGateInput {
  /** Auth has finished loading and both the signed-in user and their profile are present. */
  loaded: boolean;
  /** The Auth record's email is verified; the guide's backend refuses anyone else. */
  emailVerified: boolean;
  /** `userProfile.status`. Only active accounts use the guide (not invited, suspended or inactive). */
  accountStatus: string | undefined;
  /** `userProfile.preferences` as last read from Firestore. */
  preferences: Record<string, unknown> | null | undefined;
  pathname: string;
  /** "Finish later" or the banner's dismiss button was used in this browser session. */
  snoozed: boolean;
}

/** True once setup was completed or skipped, or the retired onboarding wizard was completed. */
export function hasFinishedOnboarding(preferences: Record<string, unknown> | null | undefined): boolean {
  if (!preferences) return false;
  if (preferences.hasCompletedOnboarding === true) return true;
  const onboarding = preferences.onboarding;
  const status =
    typeof onboarding === 'object' && onboarding !== null
      ? (onboarding as { status?: unknown }).status
      : undefined;
  return status === 'completed' || status === 'skipped';
}

export function decideOnboardingGate(input: OnboardingGateInput): OnboardingGateDecision {
  if (!input.loaded || !input.emailVerified || input.accountStatus !== 'active') return 'none';
  if (input.snoozed || hasFinishedOnboarding(input.preferences)) return 'none';

  const path = input.pathname.replace(/\/+$/, '');
  if (path === WELCOME_PATH) return 'none';
  return path === DASHBOARD_PATH ? 'redirect' : 'banner';
}
